import {
  OmniElement,
  OmniStyleElement,
  OmniTooltipElement,
  OmniLoadingIndicatorElement,
  html,
  css,
  ref,
  createRef,
  nothing,
} from 'omni-ui';
import * as d3 from 'd3';
import { rfcColor } from './channel-wheel-rfc.js';

OmniStyleElement.register();
OmniTooltipElement.register();
OmniLoadingIndicatorElement.register();

export class ChannelWheelViz extends OmniElement {
  constructor() {
    super();
    this.svgRef = createRef();
    this.selectedNodes = new Set();
    this.selectedNodesOld = new Set();
    this.rotationOffset = 0;
    this.rotationOffsetNext = 0;
    this.duration = 500;
    this.transform = { translate: 0, scale: 1 };
  }

  static get properties() {
    // TODO add property for the selected channel and translate to/from selected Nodes
    // so that it can be set externally and also updated internally.
    return {
      thoughtLeaderText: { type: String },
      thoughtLeaderLabel: { type: String },
      roleForComms: { type: String }, // activating, impacting, building, or reinforcing
      duration: { type: Number },
      disabled: { type: Boolean, reflect: true },
      channelColor: { type: Function },

      // Internal Properties
      selectedNodes: { state: true },
      rotationOffsetNext: { state: true },
      reachChannels: { state: true },
      channelWheelData: { state: true },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        svg {
          font-size: 0.607rem;
          font-weight: bold;
        }
        g text {
          fill: white;
        }
        g path {
          stroke-width: 0.5px;
          stroke: white;
        }
        g.L1 text {
          text-transform: uppercase;
        }

        g > text {
          pointer-events: none;
        }

        svg foreignObject,
        svg circle,
        g.L2,
        g.L3,
        g.L4 {
          cursor: pointer;
        }

        g.selected > path,
        g.L2 > path:hover,
        g.L3 > path:hover,
        g.L4 > path:hover {
          fill: #f2f6fa;
        }
        g.selected > text,
        g.L2 > path:hover + text,
        g.L3 > path:hover + text,
        g.L4 > path:hover + text {
          fill: black;
        }

        .thoughtleader-text {
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          margin-bottom: 0.571rem;
          text-align: center;

          @supports (-webkit-line-clamp: 2) {
            white-space: initial;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
        }

        .thoughtleader-label {
          color: rgba(var(--rgb-core-dark), 0.6);
          text-align: center;
        }

        .thoughtleader-circle {
          height: 100%;
          width: 100%;
          font-size: 0.55rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        }
        :host([disabled]) svg {
          pointer-events: none;
          opacity: 0.6;
        }
        /* Get rid of outline in chrome when focused */
        circle,
        foreignObject {
          outline: none;
        }
      `,
    ];
  }

  async updated(changedProperties) {
    if (changedProperties.has('reachChannels') && this.reachChannels) {
      const copy = {
        name: 'root',
        children: [
          {
            name: 'Paid',
            color: 'var(--color-primary)',
            children: structuredClone(this.reachChannels),
          },
        ],
      };
      this.channelWheelData = this.#prepChannelWheelData(copy);

      // Add resize observer
      new ResizeObserver(this.#transformSVG).observe(this.svgRef.value);
    } else if (this.channelWheelData) {
      this.renderD3();
    }
  }

  /**
   *  Preprocess the channel wheel data to add:
   *  - unique ids to each node (if none)
   *  - modify the "size" which controls the width when rendered
   *  - rename "activities" or "platforms" properties to "children"
   *
   *
   * @param {Object} node - The root node of our data viz data
   * @param {number} [depth] - The depth (starting at one) of the node we're
   * processing
   * @param {number} [size] - We need this to set a value on each node which is
   * used by d3.partition().
   */
  #prepChannelWheelData(node, depth = 1, size = 0) {
    const count = (this.prepChannelWheelDataCount ?? 0) + 1;
    this.prepChannelWheelDataCount = count;
    /* eslint-disable no-param-reassign */
    if (depth === 3) {
      size = 1;
    }
    if (node.activities) {
      node.children = node.activities;
      delete node.activities;
    }
    if (node.platforms) {
      node.children = node.platforms;
      delete node.platforms;
    }
    if (!node.color) {
      node.color = this.channelColor(node);
    }
    if (!node.children?.length) {
      node.value = size;
    }
    if (!node.id) node.id = `id-${count}`;
    // Delete leaf nodes (depth 5) with siblings, whose name is equal to their parent's name
    // The desire is to "hide" "duplicate" nodes in the data.
    if (depth === 4 && node.children?.length > 1) {
      node.children = node.children.filter(child => child.name !== node.name);
    }
    /* eslint-enable no-param-reassign */
    node?.children?.forEach(child =>
      this.#prepChannelWheelData(child, depth + 1, size / node.children.length),
    );
    return node;
  }

  /* Consider this a leaf node if it has no children or its on level 3 with only
   * one child with the same name as itself. This second case allows us to "hide" the
   * duplicate children on level 4.
   */
  #isLeafNode = node =>
    !node.children?.length ||
    (node.depth === 3 &&
      node.children.length === 1 &&
      node.children[0].data.name === node.data.name);

  /**
   * Called at the start of every chart (re)render. It modifies the x0, x1, y0, y1
   * values set by d3.partition() based on the depth of the node as well as what is
   * currently selected.
   *
   * @param {Object} root - The root node to our data viz data
   */
  #recalculatePositions(root) {
    /**
     * All the logic of getting a "slice"'s radius is in this helper function.
     *
     * Unfortunately the radius of each slice varies wildly depending on whether it or
     * its parent is selected, if we're ignoring its children, and additionally what
     * level it is on.
     */
    const calcRadius = (level, selected, parentSelected, isLeaf) => {
      // Slices are shorter in level 3 & 4, compute by how much
      let shrinkage = 0;
      if (level > 2) {
        shrinkage = level === 3 ? 45 : 20;
      }

      switch (level) {
        case 0:
          return 40;
        case 1:
          return 10;
        case 2:
          return selected ? 85 : 70;
        case 3:
          if (selected) return (isLeaf ? 500 : 140) - shrinkage;
          if (parentSelected) return 125 - shrinkage;
          return 5;
        case 4:
          if (selected) return (isLeaf ? 250 : 140) - shrinkage;
          if (parentSelected) return 125 - shrinkage;
          return 5;
        default:
          return 5;
      }
    };

    const processNode = (node, parentSelected, parentRadius, xScale) => {
      const selected = this.selectedNodes.has(node.data.id);
      const isLeaf = this.#isLeafNode(node);
      const nodeRadius =
        calcRadius(node.depth, selected, parentSelected, isLeaf) +
        (node.radiusIncrease ?? 0);
      /* eslint-disable no-param-reassign */
      node.y0 = parentRadius;
      node.y1 = parentRadius + nodeRadius;
      if (xScale) {
        node.x0 = xScale(node.x0);
        node.x1 = xScale(node.x1);
      }
      /* eslint-enable no-param-reassign */

      let scale = xScale;
      if (node.depth > 1 && selected && !isLeaf) {
        const fromX = xScale(node.children[0].x0);
        const toX = xScale(node.children.slice(-1)[0].x1);
        const midX = (fromX + toX) / 2;
        const depthFactor = node.depth < 3 ? 2.5 : 3;
        let newWidth = (node.children.length * (Math.PI / 6)) / depthFactor;
        const maxWidth = Math.PI / depthFactor;
        newWidth = Math.min(newWidth, maxWidth);
        scale = x =>
          d3.scaleLinear(
            [fromX, toX],
            [midX - newWidth / 2, midX + newWidth / 2],
          )(xScale(x));
      }
      node.children?.forEach?.(child =>
        processNode(child, selected, node.y1, scale),
      );
    };

    processNode(root, false, 0, x => x);
  }

  /**
   * Click handler for paths (selecting a channel). This updates our selected nodes as
   * well as sets the rotation offset.
   *
   * @param {PointerEvent} event - The click event
   * @param {object} d - The associated d3 data of the path element
   */
  #handlePathClick = (event, d) => {
    this.selectedNodes = new Set(); // Trigger rerender with new object
    for (let node = d; node.depth > 1; node = node.parent) {
      this.selectedNodes.add(node.data.id);
    }
    this.rotationOffsetNext = (d.x0 + d.x1) / -2 + Math.PI / 2;
    // Modify the next rotation to make a shorter rotation (< 180 degrees)
    while (Math.abs(this.rotationOffsetNext - this.rotationOffset) > Math.PI) {
      this.rotationOffsetNext +=
        2 *
        Math.PI *
        (this.rotationOffsetNext - this.rotationOffset > 0 ? -1 : 1);
    }

    // Dispatch an event if the selection is a leaf-node.
    // In the case that the "leaf" node actually has a child, send event as though the
    // child were clicked.
    if (this.#isLeafNode(d)) {
      const detail = { node: d.children?.length ? d.children[0] : d };
      setTimeout(
        () => this.dispatchNewEvent('channel-wheel-selection', { detail }),
        this.duration,
      );
    }
  };

  /**
   * Position (and animate) text labels for Levels 2,3,4.
   *
   * @param {object} d - The associated d3 data of the path element
   * @returns {function} returns an interpolation function that will be called by d3 during animation.
   */
  #tweenTextTransform = d => {
    let interpolateRadius0 = () => d.y0;
    const parentSelected = this.selectedNodes.has(d.parent.data.id);
    const parentWasSelected = this.selectedNodesOld.has(d.parent.data.id);
    if (d.depth > 2 && parentSelected && !parentWasSelected) {
      interpolateRadius0 = d3.interpolate(0, d.y0);
    }

    return t => {
      const x = (((d.x0 + d.x1) / 2 + this.rotationOffset) * 180) / Math.PI;
      const y = interpolateRadius0(t) + 5; // +5 to pad text from inner radius
      const flip = ((Math.round(x) % 360) + 360) % 360 < 180;
      return `rotate(${x - 90}) translate(${y},0) rotate(${flip ? 0 : 180})`;
    };
  };

  /**
   * Works in concert with #tweenTextTransform to flip the text anchor attribute so our
   * text isn't upside-down.
   *
   * @param {object} d - The associated d3 data of the path element
   * @returns {function} returns an interpolation function that will be called by d3 during animation.
   */
  #tweenTextAnchor = d => () => {
    const x = (((d.x0 + d.x1) / 2 + this.rotationOffset) * 180) / Math.PI;
    return ((Math.round(x) % 360) + 360) % 360 < 180 ? 'start' : 'end';
  };

  /**
   * Configures a d3 path selection. Calculates new radius data based on selections and
   * deals with setting up the animations. Also sets click handler and sets color
   * appropriately.
   
   * @param {Selection} selection - A d3 selection of path elements
   * @param {boolean} clickable - Whether to add a click handler
   */
  #configureArc = (selection, clickable) => {
    const arc = d3
      .arc()
      .startAngle(data => data.x0 + this.rotationOffset)
      .endAngle(data => data.x1 + this.rotationOffset);

    const arcTween = d => {
      const justSelected = node =>
        this.selectedNodes.has(node?.data?.id) &&
        !this.selectedNodesOld.has(node?.data?.id);
      const animateRadius = d.depth > 3 || (d.depth > 2 && d.y1 - d.y0 > 10);

      const interpolateRotation = d3.interpolate(
        this.rotationOffset,
        this.rotationOffsetNext,
      );

      let interpolateRadius0 = () => d.y0;
      let interpolateRadius1 = () => d.y1;
      if (
        animateRadius &&
        (justSelected(d.parent) || justSelected(d.parent.parent))
      ) {
        interpolateRadius0 = d3.interpolate(0, d.y0);
        const startRadius = d.depth === 3 ? 120 : 0;
        interpolateRadius1 = d3.interpolate(startRadius, d.y1);
      } else if (animateRadius && justSelected(d)) {
        // We're assuming there are at least two nodes at this level. The one with the
        // smaller y1 values should be the normal radius (before selection).
        // FIXME Without siblings there's no transition (start & end radiuses are equal)
        const startRadius = Math.min(
          d.parent.children[0].y1,
          d.parent.children[1]?.y1 ?? Infinity,
        );
        interpolateRadius1 = d3.interpolate(startRadius, d.y1);
      }

      return t => {
        this.rotationOffset = interpolateRotation(t);
        arc
          .innerRadius(() => interpolateRadius0(t))
          .outerRadius(() => interpolateRadius1(t));
        return arc(d);
      };
    };

    if (clickable) {
      selection.on('click', this.#handlePathClick);
    }
    selection
      .attr('fill', d => d.data.color)
      .transition()
      .duration(this.duration)
      .attrTween('d', arcTween)
      .on('end', () => {
        // After transitions are over update the "old" selected nodes.
        // Storing both is helpful to decide when certain animations should occur
        this.selectedNodesOld = this.selectedNodes;
      });
  };

  /**
   * Tween function for calculating the viewBox attribute value for animations.
   *
   * @param {Number} translate - Horizontal px translation (truncates left side of svg)
   * @param {Number} scale - Scale needed to counter-act the dimensions changing
   * @returns {Function} Returns a interpolation function which takes time (0-1).
   */
  #tweenViewBox = (translate, scale) => {
    const interpolateTranslate = d3.interpolate(
      this.transform.translate,
      translate,
    );
    const interpolateScale = d3.interpolate(this.transform.scale, scale);

    return t => {
      this.transform.translate = interpolateTranslate(t);
      this.transform.scale = interpolateScale(t);
      return `${this.transform.translate - 150} -160 ${
        520 * this.transform.scale
      } 320`;
    };
  };

  /**
   * Updates the svg viewBox attribute and if necessary applies a scale to keep the size
   * of the svg appear the same despite the dimensions changing, and a horizontal
   * translation which "truncates" the left side of the svg.
   *
   * @param {Object} args - indicates if called by the ResizeObserver (defined) or not (undefined)
   */
  #transformSVG = args => {
    const minPxWidth = 600;
    let truncate = 0;
    let scale = 1;
    if (this.clientWidth < minPxWidth) {
      truncate = minPxWidth - this.clientWidth;
      truncate = (truncate * Math.min(this.selectedNodes.size, 2)) / 2.5;
      scale = this.clientWidth / minPxWidth;
    }

    if (args) {
      // No animation, this is a resize callback
      d3.select(this.svgRef.value).attr(
        'viewBox',
        this.#tweenViewBox(truncate, scale)(1),
      );
    } else {
      d3.select(this.svgRef.value)
        .transition()
        .duration(this.duration)
        .attrTween('viewBox', () => this.#tweenViewBox(truncate, scale));
    }
  };

  truncateOverflow() {
    const self = d3.select(this);
    let textLength = self.node().getComputedTextLength();
    let text = self.text();
    let truncated = false;

    while (textLength + 6 > self.datum().y1 - self.datum().y0) {
      text = text.slice(0, -1);
      self.text(`${text}…`);
      textLength = self.node().getComputedTextLength();
      truncated = true;
    }

    // Add title element to path if truncated
    this.parentElement.querySelector('title')?.remove();
    if (truncated) {
      const title = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'title',
      );
      title.textContent = self.datum().data.name;
      this.parentElement.appendChild(title);
    }
  }

  /**
   * Draws the chart with D3 in our svg element.
   */
  renderD3() {
    const root = d3.partition().size([2 * Math.PI, 200])(
      d3
        .hierarchy(this.channelWheelData)
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value),
    );
    this.#recalculatePositions(root);

    const svg = d3.select(this.svgRef.value);
    this.#transformSVG();

    /* ****************** Level 1 Group ****************** */
    const groupsL1 = svg
      .select('g.L1s')
      .selectAll('g.L1')
      .data(root.children)
      .join('g')
      .classed('L1', true)
      .classed('selected', d => this.selectedNodes.has(d.data.id));

    // Add paths for level 1
    groupsL1
      .selectAll('path.L1')
      .data(d => [d])
      .join('path')
      .classed('L1', true)
      .attr('id', d => `path-${d.data.id}`)
      .call(this.#configureArc, false);

    // Add text label to level 1
    // TODO: Flip text https://www.visualcinnamon.com/2015/09/placing-text-on-arcs/
    groupsL1
      .selectAll('text')
      .data(d => [d])
      .join(enter => {
        const g = enter.append('text');
        g.append('textPath')
          .attr('xlink:href', d => `#path-${d.data.id}`)
          .text(d => d.data.name);
        return g;
      })
      .attr('text-anchor', 'middle')
      .attr('dy', 8)
      .attr('x', d => (Math.abs(d.x0 - d.x1) / 2) * d.y1);

    /* ****************** Level 2 Group ****************** */
    const groupsL2 = svg
      .select('g.L2s')
      .selectAll('g.L2')
      .data(root.descendants().filter(x => x.depth === 2))
      .join('g')
      .classed('L2', true)
      .classed('selected', d => this.selectedNodes.has(d.data.id));

    // Add paths for level 2
    groupsL2
      .selectAll('path')
      .data(d => [d])
      .join('path')
      .call(this.#configureArc, true);

    // Add text label to level 2
    groupsL2
      .selectAll('text')
      .data(d => [d])
      .join(enter =>
        enter
          .append('text')
          .attr('transform', d => this.#tweenTextTransform(d)(1)),
      )
      .attr('dy', 4)
      .text(d => d.data.name)
      .each(this.truncateOverflow)
      .transition()
      .duration(this.duration)
      .attrTween('text-anchor', this.#tweenTextAnchor)
      .attrTween('transform', this.#tweenTextTransform);

    /* ****************** Level 3 Group ****************** */
    const groupsL3 = svg
      .select('g.L3s')
      .selectAll('g.L3')
      .data(root.descendants().filter(x => x.depth === 3))
      .join('g')
      .classed('L3', true)
      .classed('selected', d => this.selectedNodes.has(d.data.id));

    // Add paths for level 3
    groupsL3
      .selectAll('path')
      .data(d => [d])
      .join('path')
      .call(this.#configureArc, true);

    // Add text label to level 3
    groupsL3
      .selectAll('text')
      .data(d => (d.y1 - d.y0 < 10 ? [] : [d]))
      .join(enter =>
        enter
          .append('text')
          .attr('transform', d => this.#tweenTextTransform(d)(1)),
      )
      .attr('dy', 4)
      .text(d => d.data.name)
      .each(this.truncateOverflow)
      .transition()
      .duration(this.duration)
      .attrTween('text-anchor', this.#tweenTextAnchor)
      .attrTween('transform', this.#tweenTextTransform);

    /* ****************** Level 4 Group ****************** */
    const groupsL4RFC = svg
      .select('g.L4s')
      .selectAll('g.L4rfc')
      .data(
        root
          .descendants()
          .filter(
            x =>
              x.depth === 4 &&
              rfcColor(
                x.parent.parent.data.name,
                x.parent.data.name,
                x.data.name,
                this.roleForComms,
              ),
          ),
      )
      .join('g')
      .classed('L4rfc', true);

    const createRFCData = node => {
      let { x0 } = node;
      let { x1 } = node;
      let startRadius;
      if (this.selectedNodes.has(node.data.id)) {
        startRadius = node.parent.y1 + 5;
      } else if (this.selectedNodes.has(node.parent.data.id)) {
        startRadius = node.y1;
      } else if (this.selectedNodes.has(node.parent.parent.data.id)) {
        startRadius = node.parent.y1 + 5;
      } else {
        startRadius = node.parent.parent.y1 + 5;
        x0 = node.parent.x0;
        x1 = node.parent.x1;
      }
      const color = rfcColor(
        node.parent.parent.data.name,
        node.parent.data.name,
        node.data.name,
        this.roleForComms,
      );

      return {
        ...node,
        x0,
        x1,
        y0: startRadius,
        y1: startRadius + 5,
        data: { ...node.data, color },
      };
    };

    // Add extra (rfc) paths for level 2
    groupsL4RFC
      .selectAll('path')
      .data(d => [createRFCData(d)])
      .join('path')
      .call(this.#configureArc, false);

    const groupsL4 = svg
      .select('g.L4s')
      .selectAll('g.L4')
      .data(
        root
          .descendants()
          .filter(
            x =>
              x.depth === 4 && this.selectedNodes.has(x.parent.parent.data.id),
          ),
      )
      .join('g')
      .classed('L4', true)
      .classed('selected', d => this.selectedNodes.has(d.data.id));

    // Add paths for level 4
    groupsL4
      .selectAll('path')
      .data(d => [d])
      .join('path')
      .call(this.#configureArc, true);

    // Add text label to level 4
    groupsL4
      .selectAll('text')
      .data(d => (d.y1 - d.y0 < 10 ? [] : [d]))
      .join(enter =>
        enter
          .append('text')
          .attr('transform', d => this.#tweenTextTransform(d)(1)),
      )
      .attr('dy', 4)
      .text(d => d.data.name)
      .each(this.truncateOverflow)
      .transition()
      .duration(this.duration)
      .attrTween('text-anchor', this.#tweenTextAnchor)
      .attrTween('transform', this.#tweenTextTransform);
  }

  resetSelection = () => {
    this.selectedNodes = new Set();
    this.rotationOffsetNext = 0;
  };

  get #thoughtLeaderTemplate() {
    if (!this.thoughtLeaderText?.length) return nothing;
    return html`
      <div class="thoughtleader-circle">
        <omni-tooltip class="thoughtleader-text">
          <div slot="invoker">${this.thoughtLeaderText}</div>
          <div slot="content">${this.thoughtLeaderText}</div>
        </omni-tooltip>
        <div
          class="thoughtleader-label ${this.thoughtLeaderText
            ? ''
            : 'is-hidden'}"
        >
          ${this.thoughtLeaderLabel}
        </div>
      </div>
    `;
  }

  render() {
    if (!this.reachChannels) {
      return html`
        <omni-loading-indicator
          style="margin-top: 10rem"
        ></omni-loading-indicator>
      `;
    }

    // Pre-create groups in the svg for the different levels to ensure layer order
    // is correct. Elements that occur earlier are behind newer elements.
    return html`
      <omni-style>
        <svg ${ref(this.svgRef)}>
          <g class="extras"></g>
          <g class="L4s"></g>
          <g class="L3s"></g>
          <g class="L2s"></g>
          <g class="L1s"></g>
          <circle
            tabIndex="0"
            r="40"
            fill="white"
            @click=${this.resetSelection}
            @keydown=${e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.target.click();
              }
            }}
          ></circle>
          <foreignObject
            role="button"
            tabIndex="0"
            width="66"
            height="50"
            x="-33"
            y="-25"
            @click=${this.resetSelection}
            @keydown=${e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.target.click();
              }
            }}
          >
            ${this.#thoughtLeaderTemplate}
          </foreignObject>
        </svg>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-channel-wheel-viz', ChannelWheelViz);
